/**
 * English UI copy for Bow and Arrow. Spanish is the source of truth; English
 * is a fallback for visitors who explicitly request it (?lang=en).
 *
 * Sacred-name discipline (see CLAUDE.md and docs/public-chat-architecture.md §8):
 * Yahushua, Yahweh, Padre, Todopoderoso ALWAYS appear in their canonical
 * Spanish form — NEVER as Jesus / the Lord / Jehovah / Father. The keys
 * below mirror the values in strings.es.ts intentionally.
 */
import type { Strings } from "./types";

export const stringsEn: Strings = {
  appTitle: "Arco y Flecha",
  appTagline: "Questions on Koresh's teachings, with sources.",

  idleKicker: "A Bible study instrument",
  idleTitle: "Ask, and it will be answered.",
  idleSub:
    "Koresh's teachings, gathered — to read, and to answer your questions.",
  noticeIndexingLead: "The library is still growing",
  noticeIndexingBody:
    "Koresh's teachings are being added continually. If an answer falls short or a source isn't found yet, check back in the coming days.",
  noticeDismissAria: "Dismiss notice",
  noticeLatestLabel: "Just added",
  noticeLatestChapters: (n: number): string => (n === 1 ? "1 chapter" : `${n} chapters`),
  noticeLatestAria: (book: string): string => `Read ${book} in the library`,

  composerPlaceholder: "Ask a question about Koresh's teachings…",
  composerPlaceholderDock: "Ask another…",
  composerSubmit: "Release",
  composerSubmitting: "Searching…",
  composerHintTagline: "Every question is an arrow, extending His Word throughout the world",
  composerHintEnter: "Send",
  composerCharCount: (n: number, max: number): string => `${n}/${max}`,

  threadUserLabel: "You",
  threadAssistantLabel: "Arco y Flecha",
  threadSourcesLabel: "Sources",
  threadTimestamp: (epochMs: number): string => {
    const d = new Date(epochMs);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
      ? d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  },
  threadStreamingLabel: "Composing answer…",
  threadRecheckingLabel: "Checking the sources more carefully…",
  answerIncomplete: "The answer was interrupted. Please try again.",
  composerStop: "Stop",
  composerStopAria: "Stop generating",
  answerStopped: "Answer stopped.",
  rewriteInterpretedAs: (standaloneQuery: string): string =>
    `Interpreted your question as: “${standaloneQuery}”`,
  rewriteUseExactWords: "See literal answer",
  rewriteNotIt: "If that's not it, write the whole question.",
  acknowledgeReply: (amen: boolean): string =>
    `${amen ? "Amen. " : ""}You're welcome. Ask me anything else about Koresh's teachings whenever you like.`,
  threadNewChat: "New",

  navNewChat: "New chat",
  navAbout: "About",
  navDevelopedBy: "Developed by GreenScript",
  navOfficialSite: "Camino de YHWH Congregation",
  navBiblioteca: "Library",
  corpusKicker: "Study library",
  corpusTitle: "The corpus, in order",
  corpusSubtitle: "Read Koresh's teachings in Bible order — the cleaned text that grounds every answer.",
  corpusBackToChat: "Back to chat",
  corpusOpenLibrary: "Open the library",
  corpusChaptersLabel: "Chapters",
  corpusChapterLabel: "Chapter",
  corpusChapterCount: (n: number): string => (n === 1 ? "1 chapter" : `${n} chapters`),
  corpusPrevChapter: "Previous",
  corpusNextChapter: "Next",
  corpusPageNavLabel: "Adjacent chapters",
  corpusNotIndexedNotice:
    "This chapter isn't in the chat index yet — answers to questions may not cite this section.",
  corpusShortNotIndexedNotice:
    "This reading copy is independent of the active index. This Flecha's chat availability is verified when you ask.",
  corpusShortNotFound: "This Flecha does not exist or is no longer available in the library.",
  corpusShortError: "This Flecha could not be loaded. Return to the index and try again.",
  navConexiones: "Connections",
  connectionsKicker: "Study map",
  connectionsTitle: "Scripture connections",
  connectionsSubtitle:
    "How passages connect in Koresh's teaching — derived from the corpus and hand-reviewed.",
  connectionsSearchPlaceholder: "Search a passage (e.g. Apocalipsis 6)…",
  connectionsSuggestions: "Most connected passages",
  connectionsLaneReviewed: "Reviewed",
  connectionsLaneDerived: "Derived from the corpus (automatic)",
  connectionsEvidence: "Evidence in the corpus",
  connectionsOccurrences: (n: number): string => (n === 1 ? "1 mention in the corpus" : `${n} mentions in the corpus`),
  connectionsOpenReader: "View in the library",
  connectionsAskConnection: "Ask about this connection",
  connectionsCaminoHeading: "Walk it as a guided path",
  connectionsTruncated: "Showing the strongest connections — the full neighborhood is larger.",
  connectionsLoading: "Loading the map…",
  connectionsError: "Could not load the map. Generate the graph with “npm run corpus:graph”.",
  connectionsEmpty: "No connections extracted from the corpus yet.",
  connectionsBack: "Back to search",
  connectionsStats: (edges: number, nodes: number): string =>
    `${edges} connections across ${nodes} passages`,
  corpusPrismFilterLabel: "Teaching layers",
  connectionsGroundingConnects: "Connection affirmed by Koresh",
  connectionsGroundingMentions: "Co-mentioned only (no affirmed relation)",
  connectionsGroundingUnclear: "Relation inconclusive",
  corpusGlossaryLabel: "Corpus glossary",
  navChat: "Chat",
  connectionsOnlyAffirmed: "Affirmed only",
  connectionsTrailLabel: "Trail",
  connectionsLegendAffirmed: "Affirmed",
  connectionsLegendDerived: "Derived (unjudged)",
  connectionsLegendMentions: "Co-mentioned only",
  citationViewConnections: "View connections",
  corpusContinueReading: "Continue reading",
  corpusSourceLabel: "Source",
  corpusWatchOnYouTube: "Watch on YouTube",
  corpusEmptyChapter: "This chapter has no cleaned text to show yet.",
  corpusLoading: "Loading…",
  corpusError: "Could not load the library. Generate the manifest with “npm run corpus:reader”.",
  corpusReadabilitySize: "Text size",
  corpusReadabilityLarger: "Increase text size",
  corpusReadabilitySmaller: "Decrease text size",
  corpusReadabilityType: "Toggle typeface",
  corpusBookListLabel: "Books",
  corpusAskAbout: "Ask about this",
  corpusAskPrompt: (book: string, chapter: number, section: string | null): string =>
    section !== null
      ? `Explain this section of ${book} ${chapter}: “${section}”.`
      : `Explain this section of ${book} ${chapter}.`,

  navCaminos: "Paths",
  caminosKicker: "Paths of light",
  caminosTitle: "Travel the connections",
  caminosSubtitle:
    "Guided journeys through the cosmos of connections — the camera flies passage to passage, and at every stop Koresh's actual teaching rises beside it.",
  caminosAffirmedNote: "Every hop rides a connection Koresh affirms — no path flies without evidence.",
  caminosCardStops: (n: number): string => (n === 1 ? "1 stop" : `${n} stops`),
  caminosCardMinutes: (n: number): string => `~${n} min`,
  caminosBegin: "Begin the path",
  caminosStopOf: (current: number, total: number): string => `Stop ${current} of ${total}`,
  caminosConnectedWith: (label: string): string => `Connected from ${label}`,
  caminosTaughtIn: (label: string): string => `Taught while studying ${label}`,
  caminosNext: "Next stop",
  caminosPrev: "Previous stop",
  caminosExit: "Exit",
  caminosKeysHint: "← → to navigate · Esc to exit",
  caminosProgressLabel: "Path stops",
  caminosLoading: "Loading the paths…",
  caminosError: "Could not load the paths. Generate the artifact with “npm run caminos:compile”.",
  caminosEmpty: "No promoted paths yet.",

  // El Hilo Escarlata — traced flights (Task 226).
  caminosTraceTitle: "Trace your own path",
  caminosTraceHint: "Pick two passages and fly the affirmed connection between them.",
  caminosTraceFrom: "From",
  caminosTraceTo: "To",
  caminosTraceSearchPlaceholder: "Search a passage…",
  caminosTraceGo: "Trace",
  caminosTraceBadge: "computed route",
  caminosThemeBadge: "theme",
  caminosExploreMap: "Explore on the map",
  caminosTraceReadFull: "Read the full teaching in the library ↗",
  caminosTraceMissTitle: "No affirmed route",
  caminosTraceMiss: (from: string, to: string): string =>
    `The corpus does not yet connect ${from} and ${to} through an affirmed teaching of Koresh.`,
  caminosTraceAsk: "Ask it in chat",
  caminosTraceBack: "Back to Paths",

  corpusBookmarkMenuLabel: "Save reading point",
  corpusBookmarkSave: "Save reading point",
  corpusBookmarkSaved: "Reading point saved",
  corpusBookmarkPinLabel: "Your reading point",
  corpusBookmarkJump: "Your reading point",
  corpusBookmarkJumpAria: (book: string, chapter: number): string =>
    `Return to your reading point in ${book} ${chapter}`,
  corpusBookmarkClear: "Remove",

  copy: "Copy",
  copyDone: "Copied",
  copyFailed: "Copy failed",
  shareLabel: "Share",
  shareCopied: "Link copied",
  feedbackGroupLabel: "Was this answer helpful?",
  feedbackUseful: "Helpful",
  feedbackNotUseful: "Not helpful",
  feedbackReasonsLegend: "Why wasn't it helpful?",
  feedbackReasonIncorrect: "Incorrect teaching",
  feedbackReasonMissing: "Missing teaching",
  feedbackReasonUnrelated: "Unrelated source",
  feedbackReasonSlow: "Took too long",
  feedbackNoteLabel: "Note (optional)",
  feedbackNoteHint: "Don't include personal information.",
  feedbackShareLabel: "Include my question and the answer so the team can review them.",
  feedbackSend: "Send",
  feedbackCancel: "Cancel",
  feedbackThanks: "Todá for your feedback.",
  feedbackFailed: "Couldn't send. Please try again.",
  feedbackRateLimited: "Too many in a row. Try again in a moment.",
  sharedQuestionBadge: "Shared question",

  aboutTitle: "About Arco y Flecha",
  aboutP1:
    "Arco y Flecha is a Bible-study chat interface grounded in the teachings of Koresh.",
  aboutP2:
    "Answers draw on the indexed corpus of those teachings — it is not a general Bible chatbot.",
  aboutP3:
    "Always check the cited sources. When the corpus lacks sufficient evidence, the tool says so plainly instead of inventing an answer.",
  aboutPrivacy:
    "To improve the answers, we keep the text of questions the tool could not answer at first for 30 days, with nothing about who asked them.",
  aboutClose: "Close",

  holdIdle: "Press and hold to release the arrow",
  holdCharging: "Release to send",

  sourceRailSubtitle: "Latest answer",
  sourceRailEmpty: "This answer cited no sources from the corpus.",
  sourceRailLoading: "Preparing sources",
  inlineSourcesToggle: (count: number): string => `Sources (${count})`,

  refusalHelpLabel: "What you can do",
  refusalBookLead: (passage: string): string =>
    `${passage} is in the library: open it to read, or ask about a chapter.`,
  refusalChapterLead: "Try asking about the whole chapter, or read it in the library.",
  refusalChapterMissing: (passage: string): string => `${passage} is not in the library yet.`,
  refusalBookMissing: (book: string): string => `${book} is not in the library yet.`,
  refusalHomonym: (book: string): string => `The library has ${book}, which is a different book.`,
  refusalGeneralLead:
    "Try other words, or name the book and chapter (for example, “Exodus 12”).",
  refusalTaskLead:
    "Arco y Flecha answers with what Koresh has taught in his videos; it does not do new analyses or translations. Try a short question about his teaching.",
  refusalOpenBook: (passage: string): string => `Open ${passage} in the library`,
  refusalBrowseLibrary: "See the books in the library",
  readingLinkBook: (book: string): string => `Read ${book} chapter by chapter in the library`,
  readingLinkChapter: (passage: string): string => `Read ${passage} in the library`,

  errorRateLimit: (seconds: number): string =>
    `Too many requests. Try again in ${seconds} seconds.`,
  errorQueryTooLong: (max: number): string =>
    `Query exceeds the ${max} character limit.`,
  errorTooManySources: "Too many sources requested.",
  errorNetwork: "Network error. Try again.",
  errorUnknown: "Unexpected error. Try again.",
  errorServiceUnavailable:
    "The service is temporarily unavailable. Please try again in a few minutes.",
  errorRetry: "Retry",
  errorDismiss: "Dismiss",

  citationOpenLabel: "Open on YouTube",
  citationOpenInReader: "Open in library",
  citationTimestampSeparator: "–",
  citationBookChapter: (book: string, chapter: number): string =>
    `${capitalize(book)} ${chapter}`,
  citationNoTimestamp: "No timestamp",

  relatedLabel: "Connected passages",
  relatedLead: "Connections of the passage you asked about:",
  relatedQuestion: (from: string | null, to: string): string =>
    from === null
      ? `What does Koresh teach about ${to}?`
      : `How does Koresh connect ${from} with ${to}?`,

  arrowLoadingLabel: "Drawing from the text",
  arrowLoadingLabelReduced: "Loading answer",

  langToggleLabel: "Language",
  langOptionEs: "Español",
  langOptionEn: "English",

  // Sacred-name strings. Same as strings.es.ts intentionally. Never anglicized.
  sacredYahushua: "Yahushua",
  sacredYahweh: "Yahweh",
  sacredPadre: "Padre",
  sacredTodopoderoso: "Todopoderoso",
};

function capitalize(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}
